import {ComponentChildren, createContext, RefObject, toChildArray} from "preact";
import {Children, mergeClasses} from "../types";
import {
    mdiFilterOutline,
    mdiMinusBoxOutline,
    mdiPlusBoxOutline,
    mdiSortAlphabeticalAscendingVariant,
    mdiSortAlphabeticalDescendingVariant,
    mdiSortAlphabeticalVariant
} from "@mdi/js";
import Icon from "@mdi/react";
import {useEffect, useRef, useState} from "preact/hooks";
import styled from "styled-components";
import {mix, transitions} from "polished";
import {ButtonList, IconButton} from "./button";
import {Accents, getAccentStyle, useAccent, withAccent} from "../../theme/accents";
import {JSXInternal} from "preact/src/jsx";

export type OrderDirection = "ASC" | "DESC";

export type ColumnProps<ID extends string = string> = {
    id: ID;
    title?: ComponentChildren;
    filter?: boolean;
    onFilter?: (value: string) => void;
    class?: string;
    colspan?: number;
    visible?: boolean;
    order?: boolean;
    onOrdered?: (direction: OrderDirection) => void;
}

export type Order<C extends string = string> = {
    column: C;
    direction: OrderDirection;
}

export type IsOrdered = {
    currentOrderDirection?: OrderDirection;
}

export const ColumnHeader = styled(function <ID extends string>(props: ColumnProps<ID> & IsOrdered & { className?: string }) {
    const [filterShown, _setFilterShown] = useState<boolean>(false);
    const filterRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (filterRef.current) {
            filterRef.current.focus();
        }
    });

    const setFilterShown = (newFilterShown: boolean) => {
        if (newFilterShown != filterShown) {
            _setFilterShown(newFilterShown);
            if (!newFilterShown) {
                props.onFilter && props.onFilter("");
            } else if (filterRef.current) {
                props.onFilter && props.onFilter(filterRef.current.value);
            }
        }
    }

    const addons: ComponentChildren[] = [];
    const additionalContent: ComponentChildren[] = [];
    let classes = props.class;

    if (props.order) {
        if (props.currentOrderDirection) {
            classes = mergeClasses([`order-${props.currentOrderDirection.toLowerCase()}`], classes);
        }

        let path = mdiSortAlphabeticalVariant;
        if (props.currentOrderDirection === "ASC") {
            path = mdiSortAlphabeticalAscendingVariant;
        } else if (props.currentOrderDirection === "DESC") {
            path = mdiSortAlphabeticalDescendingVariant;
        }

        addons.push(<IconButton
            className="btn order-icon"
            onClick={() => {
                if (props.onOrdered) {
                    props.onOrdered(props.currentOrderDirection === "ASC" ? "DESC" : "ASC");
                }
            }}
            path={path}
        />);
    }

    if (props.filter) {
        classes = mergeClasses(["with-incsearch"], classes);
        addons.push(<IconButton
            className="btn"
            title="Quick filter"
            onClick={() => setFilterShown(!filterShown)}
            path={mdiFilterOutline}
        />);

        if (filterShown) {
            additionalContent.push(<div class="incsearch-wrapper">
                <input
                    ref={filterRef}
                    type="text"
                    onInput={(e) => {
                        props.onFilter && props.onFilter(e.currentTarget.value);
                    }}
                />
            </div>);
        }
    }

    if (addons.length > 0) {
        return <th className={`${classes} ${props.className}`} colspan={props.colspan}>
            <div class="title-with-addon">
                {props.title}
                {addons}
            </div>
            {additionalContent}
        </th>;
    } else {
        return <th class={props.class}>{props.title}{additionalContent}</th>
    }
})`
    &.with-incsearch {
        position: relative;
        
        .incsearch-wrapper {
            position: absolute;
            left: 0.5rem;
            top: 0.5rem;
            right: 2rem;
            background: inherit;
            
            input {
                border-bottom-color: #ecf0f1;
            }
        }
    }
    
    .order-icon {
        visibility: hidden;
    }
    
    &.order-asc, &.order-desc, &:hover {
        .order-icon {
            visibility: visible;
        }
    }
    
    .title-with-addon {
        display: flex;
        
        >:first-child {
            margin-left: auto;
        }
    }
`;

export type RowProps = {
    cells: { [key: string]: ComponentChildren};
    class?: string;
    className?: string;
};

export function Row(props: RowProps) {
    return <TableContext.Consumer>{({table}) =>
        <tr class={props.class} className={props.className}>{table?.props.columns
            .filter(column => column.visible ?? true)
            .map(column => props.cells[column.id] ?? <td></td>)
        }</tr>
    }</TableContext.Consumer>;
}

type CollapsibleRowProps = {
    title: string
    colspan?: number;
    collapsed?: boolean;
    onToggle?: (collapsed: boolean) => void;
} & Children;

const RowGroup = styled.th`
    &&&& {
        background: ${props => mix(0.3, props.theme.colors.primaryBg, props.theme.colors.cardBg)};
        cursor: pointer;
        
        h2 {
            display: flex;
            align-items: center;
            gap: .2rem;
            font-size: 1rem;
            margin: 0;
        }
        
        &:hover {
            background: ${props => props.theme.colors.hover(mix(0.3, props.theme.colors.primaryBg, props.theme.colors.cardBg))};
        }
    }
`;

export function CollapsibleRow(props: CollapsibleRowProps) {
    const colspan = props.colspan ?? 1;

    const toggle = () => {
        if (props.onToggle) {
            props.onToggle(!props.collapsed);
        }
    }

    if (toChildArray(props.children).length === 0) {
        return <></>;
    }

    return <>
        <tr>
            <RowGroup colspan={colspan} onClick={() => toggle()}>
                <h2>
                    <Icon size="14px" path={props.collapsed ? mdiPlusBoxOutline : mdiMinusBoxOutline } className="mdi" />
                    {props.title}
                </h2>
            </RowGroup>
        </tr>
        {!props.collapsed ? props.children : null}
    </>;
}

export type TableContext<O extends string = string> = {
    table?: {props: TableProps<O>}
};

const TableContext = createContext<TableContext>({});

export type TableProps<ColumnIDs extends string = string> = {
    columns: ColumnProps<ColumnIDs>[];
    context?: TableContext<ColumnIDs>;
    onFilter?: (column: ColumnProps<ColumnIDs>, value: string) => void;
    footer?: ComponentChildren;
    onOrdered?: (order: Order<ColumnIDs>) => void;
    order?: Order<ColumnIDs>;
    showHeader?: boolean;
    className?: string;
} & Children;

export const Table = styled(withAccent(function<O extends string = string> (props: TableProps<O>) {
    return <TableContext.Provider value={{table: {props: props as TableProps}}}>
        <table className={props.className}>
            {props.showHeader ?? true ?
                <thead>
                    <tr>
                        {props.columns
                            .filter(column => column.visible ?? true)
                            .map(column =>
                                <ColumnHeader<O>
                                    {...column}
                                    currentOrderDirection={(props.order?.column === column.id ? props.order.direction : undefined)}
                                    onFilter={(v: string) => { props.onFilter && props.onFilter(column, v) }}
                                    onOrdered={(direction: OrderDirection) => props.onOrdered && props.onOrdered({column: column.id, direction: direction})}
                                />
                            )}
                    </tr>
                </thead>
                : null
            }
            {props.footer ? <tfoot>{props.footer}</tfoot> : null}
            <tbody>
                {props.children}
            </tbody>
        </table>
        <div class="table-spacer"></div>
    </TableContext.Provider>;
}, "primary") as <T extends string = string>(p: TableProps<T> & { ref?: RefObject<HTMLTableElement> }) => JSXInternal.Element)`
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    
    > thead {
        th, td {
            background: ${props => getAccentStyle(props).primaryBg};
            color: ${props => getAccentStyle(props).primaryFg};
        }
    }
    
    > tbody th {
        text-align: left;
    }
    
    tr {
        height: 1px;
    }
    
    td, th {
        padding: .5rem;
        height: inherit;
        
        & > .number:only-child {
            display: block;
            text-align: right;
        }
        
        &:has(${ButtonList}) {
            padding: 0;
        }
        
        ${transitions(['color', 'background-color'], '.075s ease-in-out')}
    }
    
    > tbody {
        td, th {
            background: ${props => getAccentStyle(props).cardBg};
            color: ${props => getAccentStyle(props).cardFg};
        }
        
        tr:not(.table-nohover):hover {
            td, th {
                background: ${props => props.theme.colors.hover(getAccentStyle(props).cardBg)};
            }
        }
        
        tr:nth-child(2n) {
            td, th {
                background: ${props => props.theme.colors.even(getAccentStyle(props).cardBg)};
            }
            
            &:not(.table-nohover):hover {
                td, th {
                    background: ${props => props.theme.colors.hover(props.theme.colors.even(getAccentStyle(props).cardBg))};
                }
            }
        }
    }
`;
